import React, { useCallback, useMemo } from 'react';
import {
  Box,
  FormControl,
  IconButton,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { capitalizeFirstLetter } from '../../../../utils/capitalizeFirstLetter';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useController, useFieldArray } from 'react-hook-form';
import { InputFactory } from './InputFactory';
import { Button } from 'react-admin';
import DeleteIcon from '@mui/icons-material/Close';
import {
  checkIsExpandedAccordion,
  createIsRequiredBlockCheck,
  createRemoveFiled,
  createShouldShowField,
} from './utils';

const getFullItemPath = (parentPath, itemName) => {
  return parentPath ? `${parentPath}.${itemName}` : itemName;
};

const FormSection = ({
  form,
  formSettingsItem,
  expandedSections,
  parentPath = '',
  onToggleExpand,
}) => {
  const { control, unregister, getValues } = form;

  const sectionName = getFullItemPath(parentPath, formSettingsItem.name);
  const isMultiple = formSettingsItem.single === false;

  const { fields, append, remove } = useFieldArray({
    control,
    name: sectionName,
  });

  const shouldShowField = useCallback(
    createShouldShowField(getValues, sectionName),
    [sectionName]
  );

  const checkIsRequiredBlock = createIsRequiredBlockCheck(
    getValues,
    sectionName
  );

  const removeFiled = createRemoveFiled(getValues, unregister, sectionName);

  const handleAddItem = useCallback(() => {
    const newItem = {};
    formSettingsItem.fields?.flat()?.forEach((item) => {
      newItem[item.key] = item.defaultValue ?? '';
    });
    append(newItem);
  }, [formSettingsItem.fields, append]);

  return (
    <>
      {isMultiple ? (
        <>
          {fields.map((field, fieldIndex) => (
            <MultipleField
              key={field.id}
              field={field}
              fieldIndex={fieldIndex}
              formSettingsItem={formSettingsItem}
              sectionName={sectionName}
              control={control}
              shouldShowField={shouldShowField}
              isRequiredBlock={checkIsRequiredBlock(
                formSettingsItem,
                fieldIndex
              )}
              onRemove={remove}
              unregister={removeFiled}
              form={form}
              parentPath={parentPath}
              expandedSections={expandedSections}
              onToggleExpand={onToggleExpand}
            />
          ))}
          <Button
            variant="outlined"
            sx={{
              background: '#F7F7F7',
              color: '#000',
              border: 'none',
              height: '36px',
              fontSize: '16px',
              ml: 3,
              mr: 3,
              mb: 3,
              '&:hover': {
                border: 'none',
              },
              svg: {
                transform: 'translateY(2px)',
              },
            }}
            onClick={handleAddItem}
          >
            <AddIcon /> Add Item
          </Button>
        </>
      ) : (
        <SingleFields
          formSettingsItem={formSettingsItem}
          sectionName={sectionName}
          control={control}
          shouldShowField={shouldShowField}
          isRequiredBlock={checkIsRequiredBlock(formSettingsItem)}
          unregister={removeFiled}
          form={form}
          parentPath={parentPath}
          expandedSections={expandedSections}
          onToggleExpand={onToggleExpand}
        />
      )}
    </>
  );
};

const FieldControl = React.memo(
  ({ item, fieldName, control, itemIndex, isRequired }) => {
    const validationRules = useMemo(() => {
      const rules = {};

      // Required validation
      if (isRequired) {
        rules.required = 'This field is required.';
      } else {
        // clear required state if it was changed
        rules.required = null;
      }

      // Min validation (use undefined, min can be 0)
      if (item.rules?.min !== undefined) {
        if (item.type === 'number') {
          // For number fields - minimum value
          rules.min = {
            value: item.rules.min,
            message: `Minimum value is ${item.rules.min}`,
          };
        } else {
          // For text fields - minimum length
          rules.minLength = {
            value: item.rules.min,
            message: `Minimum length is ${item.rules.min} characters`,
          };
        }
      }

      // Max validation (use undefined, max can be 0)
      if (item.rules?.max !== undefined) {
        if (item.type === 'number') {
          // For number fields - maximum value
          rules.max = {
            value: item.rules.max,
            message: `Maximum value is ${item.rules.max}`,
          };
        } else {
          // For text fields - maximum length
          rules.maxLength = {
            value: item.rules.max,
            message: `Maximum length is ${item.rules.max} characters`,
          };
        }
      }

      // Regex validation
      if (item.rules?.regex) {
        rules.pattern = {
          value: new RegExp(item.rules.regex),
          message: 'Invalid format',
        };
      }

      return rules;
    }, [item.rules, item.type, isRequired]);

    const {
      field,
      fieldState: { error },
    } = useController({
      name: fieldName,
      control,
      rules: validationRules,
    });

    return (
      <FormControl
        fullWidth
        key={item.key || itemIndex}
        sx={{ mb: 2 }}
        error={!!error}
      >
        <InputFactory
          item={item}
          field={field}
          control={control}
          error={error}
          readonly={item.readonly}
          multiple={item.multiple}
        />
      </FormControl>
    );
  }
);

const MultipleField = React.memo(
  ({
    field,
    fieldIndex,
    formSettingsItem,
    expandedSections,
    onToggleExpand,
    sectionName,
    control,
    shouldShowField,
    isRequiredBlock,
    onRemove,
    unregister,
    form,
  }) => {
    const visibleFields =
      formSettingsItem.fields?.flat().filter((item) => {
        const isShow = shouldShowField(item, fieldIndex);

        if (!isShow) {
          unregister(item, fieldIndex);
        }
        return isShow;
      }) || [];

    return (
      <Box
        key={field.id}
        sx={{
          display: 'flex',
          alignItems: 'center',
          pl: 3,
          pr: 3,
          mb: 2,
        }}
      >
        <Box
          sx={{
            mb: 2,
            border: '1px dashed #ccc',
            p: 2,
            flex: 1,
          }}
        >
          {visibleFields.map((item, itemIndex) => {
            const fieldName = getFullItemPath(
              sectionName,
              `${fieldIndex}.${item.key}`
            );

            return (
              <FieldControl
                key={item.key}
                item={item}
                fieldName={fieldName}
                control={control}
                itemIndex={itemIndex}
                isRequired={isRequiredBlock && item.required}
              />
            );
          })}

          {/* Render nested groups */}
          <NestedGroups
            groups={formSettingsItem.groups}
            form={form}
            parentPath={getFullItemPath(sectionName, fieldIndex)}
            expandedSections={expandedSections}
            onToggleExpand={onToggleExpand}
          />
        </Box>
        <IconButton
          size="small"
          onClick={() => onRemove(fieldIndex)}
          sx={{
            ml: 2,
            width: 36,
            height: 36,
            backgroundColor: '#fdecea',
            color: '#d32f2f',
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: '#f9d6d5',
            },
          }}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  }
);

const SingleFields = React.memo(
  ({
    formSettingsItem,
    expandedSections,
    onToggleExpand,
    sectionName,
    control,
    shouldShowField,
    isRequiredBlock,
    unregister,
    form,
  }) => {
    const visibleFields =
      formSettingsItem.fields?.flat().filter((item) => {
        const isShow = shouldShowField(item);

        if (!isShow) {
          unregister(item);
        }
        return isShow;
      }) || [];

    return (
      <>
        {visibleFields.map((item, itemIndex) => {
          const fieldName = `${sectionName}.${item.key}`;

          return (
            <Box key={item.key || itemIndex} sx={{ pl: 3, pr: 3, mb: 2 }}>
              <FieldControl
                item={item}
                fieldName={fieldName}
                control={control}
                itemIndex={itemIndex}
                isRequired={isRequiredBlock && item.required}
              />
            </Box>
          );
        })}

        {/* Render nested groups */}
        <NestedGroups
          groups={formSettingsItem.groups}
          form={form}
          parentPath={sectionName}
          expandedSections={expandedSections}
          onToggleExpand={onToggleExpand}
        />
      </>
    );
  }
);

const NestedGroups = React.memo(
  ({ groups, form, parentPath, expandedSections, onToggleExpand }) => {
    if (
      !groups ||
      typeof groups !== 'object' ||
      Object.keys(groups).length === 0
    ) {
      return null;
    }

    return (
      <>
        {Object.entries(groups).map(([groupKey, groupItems]) => {
          if (!Array.isArray(groupItems) || groupItems.length === 0) {
            return null;
          }

          return (
            <Box key={groupKey} sx={{ m: 3 }}>
              {groupItems.map((groupItem) => {
                const fullSectionPath = getFullItemPath(
                  parentPath,
                  groupItem.name
                );
                const expanded = checkIsExpandedAccordion(
                  expandedSections,
                  fullSectionPath
                );
                const shouldShowBlock = createShouldShowField(
                  form.getValues,
                  groupItem.name
                )(groupItem);

                if (!shouldShowBlock) {
                  return null;
                }

                return (
                  <Accordion
                    key={groupItem.name}
                    id={fullSectionPath}
                    expanded={expanded}
                    onChange={(_, isExpanded) => {
                      onToggleExpand(fullSectionPath, isExpanded);
                    }}
                    sx={{
                      '&.Mui-expanded': {
                        background: 'rgba(235, 235, 235, 0.25)',
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        p: 2,
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={600}>
                        {capitalizeFirstLetter(
                          groupItem.label || groupItem.name
                        )}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <FormSection
                        form={form}
                        formSettingsItem={groupItem}
                        parentPath={parentPath}
                        expandedSections={expandedSections}
                        onToggleExpand={onToggleExpand}
                      />
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          );
        })}
      </>
    );
  }
);

export default FormSection;
